import { useRouter } from 'next/router'
import { ReactNode, useEffect, useState, VFC } from 'react'
import {
  AppointmentIcon,
  DisableAppointmentIcon,
  DisableTagsManagementIcon,
  LeftArrowIcon,
  ReportUserHoverIcon,
  ReportUserIcon,
  SmallUserIcon,
  TagsManagementIcon,
} from 'src/assets/svgs'
import { UserIcon } from 'src/components/Image/UserIcon'
import { TooltipWrapper } from 'src/components/Tooltip'
import { TagsManagement } from 'src/compositions/Messages/TagsManagement'
import { Counter, Room, TagMapping } from 'src/models/Room'
import { _messagesecondary } from 'src/styles/colors'
import { fontWeightBold } from 'src/styles/font'
import { flexCenter, textEllipsis } from 'src/styles/mixins'
import { MESSAGES } from 'src/utils/routes'
import styled from 'styled-components'
import { SWRInfiniteResponse } from 'swr/infinite/dist/infinite'
import { useMessageContext } from '../MessageContext'
import { DEFAULT_TAB_TYPE, TabType } from '../SubMenu/TabList'

export { MESSAGE_HEADER_HEIGHT, MessageHeader }
export type HeaderProps = {
  counters: Counter[] | undefined
  toggleSubMenu: VoidFunction
  openReportUserModal: (id: number) => void
  isGroup: boolean
  isReported: boolean | undefined
  isSubMenuOpen: boolean
  feedbacked: boolean
  canFeedback: boolean
  hasDeletedUser: boolean
  activeTab: TabType
  setTabType: (tabType: TabType) => void
  paid: boolean
  mutateRooms: SWRInfiniteResponse<Room[], any>['mutate']
  showProfile: (userId: number) => void
}

const HeaderComponent: VFC<HeaderProps & { className?: string }> = ({
  counters = [],
  isGroup,
  toggleSubMenu,
  openReportUserModal: openReportModal,
  isReported,
  isSubMenuOpen,
  feedbacked,
  canFeedback,
  hasDeletedUser,
  activeTab,
  setTabType,
  paid,
  mutateRooms,
  showProfile,
  className,
}) => {
  const notDeleted = !hasDeletedUser

  const { room } = useMessageContext()

  // NOTE: ルーム開封or変更時にタブを自動的に切り替える処理(useEffectの対象範囲はroom変更時のみ)
  // 1. フィードバック可能な場合は常にフィードバックタブを開く
  // 2. ルーム開封にフィードバック不可の場合はタブを開かない
  // 3. フィードバック可能なルームを開いている時にルーム変更を行い、フィードバック不可の場合はデフォルトタブを開く
  useEffect(() => {
    if (room?.canFeedback) {
      if (!isSubMenuOpen) toggleSubMenu()
      setTabType('FeedbackTab')
    } else setTabType(DEFAULT_TAB_TYPE)
    setDisplayTagsManagement(false)
  }, [room?.id, room?.canFeedback])

  const [displayTagsManagement, setDisplayTagsManagement] =
    useState<boolean>(false)
  const [tagMappings, setTagMappings] = useState<TagMapping[]>([])

  useEffect(() => {
    setTagMappings(room?.tagMappings || [])
  }, [room?.tagMappings])

  // toggleTab: タブを状態によって開閉します
  // 1. 現在開いているタブをクリックしたらタブを閉じる
  // 2. タブを閉じていたら、クリックしたタブを開きます
  // 3. 別タブを開いてる時にクリックしたタブを開きます
  const toggleTab = (tab: TabType) => {
    if (activeTab === tab && isSubMenuOpen) toggleSubMenu()
    if (!isSubMenuOpen) toggleSubMenu()
    setTabType(tab)
  }

  const users = counters.filter((counter) => !counter.isReferrer)
  const router = useRouter()

  return (
    <>
      <HeaderDiv className={className}>
        <Left>
          <LeftArrowDiv onClick={() => router.push(MESSAGES)}>
            <LeftArrowIcon />
          </LeftArrowDiv>
          <div>
            {isGroup ? (
              <GroupRoomHeading counters={users} showProfile={showProfile} />
            ) : (
              users[0] && (
                <RoomHeading counter={users[0]} showProfile={showProfile} />
              )
            )}
          </div>
        </Left>
        <Right>
          <>
            {/* NOTE: 有料会員のみタグ管理アイコンを表示 */}
            {paid && (
              <MessageHeaderTooltip tooltip="タグ管理" xValue={-20}>
                {/* NOTE: room 未選択時はタグ管理アイコンを disabled にする */}
                {room ? (
                  <button
                    onClick={() =>
                      setDisplayTagsManagement(!displayTagsManagement)
                    }
                  >
                    <TagsManagementIcon />
                  </button>
                ) : (
                  <button disabled>
                    <DisableTagsManagementIcon />
                  </button>
                )}
              </MessageHeaderTooltip>
            )}
            {room && displayTagsManagement && (
              <TagsManagement
                tagMappings={tagMappings}
                setTagMappings={(tagMappings: TagMapping[]) =>
                  setTagMappings([...tagMappings])
                }
                mutateRooms={() => mutateRooms()}
                roomId={room.id}
                roomType={room.type}
                positions={{ top: 40, right: 10 }}
                close={() => setDisplayTagsManagement(false)}
              />
            )}
          </>

          {notDeleted &&
            (canFeedback ? (
              <MessageHeaderTooltip tooltip={'ユーザーを評価する'} xValue={-50}>
                <button onClick={() => toggleTab('FeedbackTab')}>
                  <AppointmentIcon />
                </button>
              </MessageHeaderTooltip>
            ) : (
              <MessageHeaderTooltip
                tooltip={feedbacked ? '評価済' : '評価不可'}
                xValue={-20}
              >
                <button disabled>
                  <DisableAppointmentIcon />
                </button>
              </MessageHeaderTooltip>
            ))}

          {/* NOTE: 3社間 or 紹介者アカウント では通報ボタンを表示しない */}
          {!isGroup && !counters.some((c) => c.isAdminRole) && (
            <MessageHeaderTooltip
              tooltip={isReported ? '通報済み' : 'ユーザーを通報'}
              xValue={isReported ? -20 : -60}
            >
              <ReportUserButton
                isReported={isReported}
                openReportUserModal={openReportModal}
                users={users}
              />
            </MessageHeaderTooltip>
          )}
          <MessageHeaderTooltip tooltip="ルーム情報" xValue={-60}>
            <button onClick={() => toggleTab('Profile')}>
              <SmallUserIcon />
            </button>
          </MessageHeaderTooltip>
        </Right>
      </HeaderDiv>
    </>
  )
}

const MessageHeader = styled(HeaderComponent)``

const RoomHeading: VFC<{
  counter: Counter
  showProfile: (userId: number) => void
}> = ({
  counter: { id, companyName, position, name, iconSrc },
  showProfile,
}) => (
  <RoomHeadingDiv>
    <UserIcon src={iconSrc} size="40px" onClick={() => showProfile(id)} />
    <RoomHeadingButton type="button" onClick={() => showProfile(id)}>
      <h2>{companyName}</h2>
      <h3>{position ? `${name} / ${position}` : name}</h3>
    </RoomHeadingButton>
  </RoomHeadingDiv>
)

const GroupRoomHeading: VFC<{
  counters: Counter[]
  showProfile: (userId: number) => void
}> = ({ counters, showProfile }) => (
  <>
    {counters.length === 1 && (
      <GroupLabel>株式会社オンリーストーリー作成の3社間グループ</GroupLabel>
    )}
    {counters.map((counter) => (
      <GroupRoomHeadingButton
        key={counter.id}
        type="button"
        onClick={() => showProfile(counter.id)}
      >
        {counter.companyName} {counter.name}
        {counter.position ? ` / ${counter.position}` : ''}
      </GroupRoomHeadingButton>
    ))}
  </>
)

type MessageHeaderTooltipProps = {
  tooltip: string
  xValue: number
  children: ReactNode
}
const MessageHeaderTooltip: VFC<MessageHeaderTooltipProps> = ({
  tooltip,
  xValue,
  children,
}) => (
  <MessageHeaderTooltipWrapper>
    <TooltipWrapper
      tooltipNode={tooltip}
      type={'messageHeader'}
      direction={{ y: 'bottom', x: 'right' }}
      yValue="26px"
      xValue={`${xValue}px`}
    >
      {children}
    </TooltipWrapper>
  </MessageHeaderTooltipWrapper>
)

type ReportUserButtonProps = {
  isReported: boolean | undefined
  openReportUserModal: (id: number) => void
  users: Counter[]
}
const ReportUserButton: VFC<ReportUserButtonProps> = ({
  isReported,
  openReportUserModal,
  users,
}) => (
  <>
    {isReported ? (
      <button
        onClick={() => openReportUserModal(users && users[0].id)}
        disabled={true}
      >
        <ReportUserIcon />
      </button>
    ) : (
      <button
        onClick={() => openReportUserModal(users && users[0].id)}
        disabled={users && users.length == 0}
      >
        <ReportUserIcons>
          <ReportUserIcon />
          <HoveredIcon>
            <ReportUserHoverIcon />
          </HoveredIcon>
        </ReportUserIcons>
      </button>
    )}
  </>
)

const ReportUserIcons = styled.div`
  position: relative;
  max-width: 100%;
`

const HoveredIcon = styled.div`
  position: absolute;
  inset: 0;
  object-fit: contain;
  opacity: 0;
  transition: opacity 0.2s;
  :hover {
    opacity: 1;
  }
`

const GroupLabel = styled.p`
  color: ${_messagesecondary};
  font-size: 12px;
  font-weight: ${fontWeightBold};
`
const Left = styled.div`
  display: flex;
  align-items: center;
`

const LeftArrowDiv = styled.div`
  display: none;

  @media screen and (max-width: 960px) {
    display: block;
    padding: 16px;
    cursor: pointer;
  }
`

const Right = styled.div`
  display: flex;
  justify-content: space-between;

  button {
    ${flexCenter};
    flex-direction: column;
    margin-top: 4px;
    margin-left: 10px;
    font-size: 10px;
  }
  p {
    margin-top: 4px;
    white-space: nowrap;
  }

  @media screen and (max-width: 960px) {
    display: none;
  }
`

const MESSAGE_HEADER_HEIGHT = '72px'
const HeaderDiv = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 2;

  height: ${MESSAGE_HEADER_HEIGHT};
  @media screen and (max-width: 960px) {
    height: 56px;
  }

  background-color: #ffffff40;
  backdrop-filter: blur(8px) brightness(1.25);

  font-weight: ${fontWeightBold};

  h2 {
    margin-top: 4px;
    font-size: 18px;
    ${textEllipsis};
  }
  h3 {
    margin-top: 8px;
    font-size: 14px;
    ${textEllipsis};
  }

  @media screen and (max-width: 960px) {
    h2 {
      margin-top: 0px;
      font-size: 14px;
    }
    h3 {
      margin-top: 4px;
      font-size: 12px;
    }
  }

  ${Left} {
    width: calc(100% - 50px);
  }
  ${Right} {
    flex-shrink: 0;
  }
`
const MessageHeaderTooltipWrapper = styled.div`
  padding-left: 6px;
`
const RoomHeadingDiv = styled.div`
  display: flex;
  img {
    display: none;

    @media screen and (max-width: 960px) {
      display: block;
      margin-right: 8px;
    }
  }
`

const RoomHeadingButton = styled.button`
  min-width: 0;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
`

const GroupRoomHeadingButton = styled.button`
  display: block;
  max-width: 100%;
  margin-top: 4px;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 18px;
  text-align: left;
  cursor: pointer;
  ${textEllipsis};

  @media screen and (max-width: 960px) {
    margin-top: 0px;
    font-size: 14px;
  }
`
